import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";

import pauseIcon from "@phosphor-icons/core/fill/pause-fill.svg";
import playIcon from "@phosphor-icons/core/fill/play-fill.svg";

interface Props {
    /**
     * Indicates whether the button is disabled.
     */
    disabled?: boolean;

    /**
     * Indicates whether the media is currently playing. If true, the button
     * will show a "pause" icon.
     */
    playing: boolean;

    /**
     * Optional ID for testing.
     */
    testId?: string;

    /**
     * Determines the placement of the tooltip. This should be one of the valid
     * placements for a Tooltip component.
     */
    tooltipPlacement?: React.ComponentProps<typeof Tooltip>["placement"];

    /**
     * Optional styles to be applied to the button.
     */
    style?: StyleType;

    /**
     * Callback function that is called when the button is clicked and the
     * media is currently playing. This should pause the media.
     */
    onPause: () => unknown;

    /**
     * Callback function that is called when the button is clicked and the
     * media is currently paused. This should start playing the media.
     */
    onPlay: () => unknown;
}

/**
 * A play/pause button.
 *
 * Render play/pause button for audio and video media. This is used in the
 * MessageBubble component.
 */
export const PlaybackButton = (props: Props) => {
    const {
        playing,
        disabled,
        style,
        testId,
        tooltipPlacement,
        onPause,
        onPlay,
    } = props;

    return (
        <Tooltip
            content={playing ? t`Pause` : t`Replay this message`}
            placement={tooltipPlacement}
        >
            <IconButton
                disabled={disabled}
                icon={playing ? pauseIcon : playIcon}
                kind="tertiary"
                size="xsmall"
                style={style}
                testId={testId}
                onClick={playing ? onPause : onPlay}
            />
        </Tooltip>
    );
};
